import { TrophyHall, trophiesSection, trophyHallFromSave } from "@highmarch/app";
import { openShops } from "../ui/shops/shopsScreen";
import { TrophyLink, setTrophyLink, trophyLink } from "../ui/trophy/trophyLink";
import { trophiesGlyph } from "../ui/hub/eventRewards";
import { setTrophyHallOpener } from "../ui/hub/trophySkins";
import { rowHtml } from "../ui/components";
import { btnHtml, fmtCompact } from "../ui/kit";
import { toast } from "../ui/toast";
import type { Feature, HubApi } from "./registry";

/**
 * The Trophy Hall (MP-23, docs/SHOP.md §9.7): Trophies' Hall in the Shops sheet (its fourth tab, ui/trophy/
 * trophyHallPage.ts), reached from the Shops (Shop tab → Marks → Trophy Hall, ⚙/More → Shops) and from a small
 * "Trophy Hall" row under an event mode's panel on the Events sheet (hub only: a link the player taps, never a pop-up).
 * The `trophies` save section bound to the save; the Hall opens for good the moment the first Trophies are held (Captain
 * Bram's line about them, ui/guide/scripts/trophies.ts, waits for that). A Guide topic, and debug hooks for captures.
 */

/** Under the event mode's own panel (features/eventModes.ts PANEL_ORDER 10). */
const PANEL_ORDER = 11;
/** The event-mode weeks whose panel the Trophy Hall row sits under (rotation kinds, events.json). */
const MODE_KINDS: readonly string[] = ["boss", "race", "odyssey"];
/** The Guide topic opens with the Hall. */
const TOPIC_FROM_LEVEL = 10;
const LEAD_PX = 30;

/** Whether the game holds Trophies now (Captain Bram's first line about them; features/guide.ts reads it). */
export function trophiesHeld(): number { return trophyLink()?.hall.trophies ?? 0; }

export const trophyHallFeature: Feature = {
  id: "trophyHall",
  register(ctx, registry) {
    const hall = new TrophyHall(ctx.catalog, trophyHallFromSave(ctx.save));
    trophiesSection.bind(ctx.save, () => hall.state.toJson());
    const link: TrophyLink = {
      hall, catalog: ctx.catalog, now: ctx.now, hub: null,
      changed: () => { if (link.hub) link.hub.changed(); else ctx.persist(); },
    };
    setTrophyLink(link);
    // The first Trophies open the Hall for good (noted once; the caller saves).
    const noteMet = (): void => { if (hall.noteMet()) ctx.persist(); };
    registry.battleEnd(() => noteMet());
    registry.hubWidget({
      id: "trophyHall",
      mount: (hub) => { link.hub = hub; noteMet(); return { dispose: () => { if (link.hub === hub) link.hub = null; } }; },
    });

    const open = (hub: HubApi | null): void => {
      if (hub) link.hub = hub;
      if (!openShops("trophy")) toast(ctx.ui, "The Trophy Hall opens in the hub");
    };
    // The Skins picker's TROPHY HALL button on a locked Trophy Hall exclusive (ui/hub/trophySkins.ts) opens the Hall.
    setTrophyHallOpener(() => open(null));
    registry.eventPanel({
      id: "trophyHall", order: PANEL_ORDER,
      html: (e) => {
        if (!MODE_KINDS.includes(e.kind) || !hall.open) return "";
        const n = hall.trophies;
        return `<div class="trlinkcard">${rowHtml({ lead: trophiesGlyph(LEAD_PX), title: "Trophy Hall", sub: `${n > 0 ? `${fmtCompact(n)} Trophies held · ` : ""}honours and the Armoury`, end: btnHtml("OPEN", "blue", { className: "disp trlink" }), compact: true, className: "trlinkrow" })}</div>`;
      },
      bind: (panel, api) => {
        panel.querySelector<HTMLButtonElement>(".trlink")?.addEventListener("click", () => { api.close(); open(link.hub); });
      },
    });

    registry.guideTopic({
      id: "trophies", group: "castle", label: "Trophies", art: { icon: "shield" }, fromLevel: TOPIC_FROM_LEVEL, steps: ["hub.trophies"],
      lines: [
        "**Trophies** are won in the hardest weekly fights: Boss Week, Race Week, the Odyssey, the Trial, Endless and Iron.",
        "Spend them in the **Trophy Hall** (Shop → Shops): exclusive cosmetics, and a weekly **Armoury** of power.",
      ],
    });

    if (ctx.debug && typeof window !== "undefined") {
      (window as unknown as { __tdTrophy: unknown }).__tdTrophy = {
        hall, link, open: () => openShops("trophy"), view: () => hall.view(ctx.now()),
        /** Captures: Trophies without playing. */
        grant: (n: number) => { ctx.catalog.economy.wallet.grant("trophies", n, "debug", ctx.now()); noteMet(); link.changed(); },
      };
    }
  },
};
